import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Folder, Lock, Users } from 'lucide-react';
import { toolLogo } from './AppChrome';

/**
 * The simplest form of the idea: a row of sources as bare circles, tracks as
 * columns below, and observations visibly travelling from one to the other.
 *
 * No labels or cadences on the sources — the logo is the whole point, and the
 * text belongs on the observation that comes out. No outer panel either, so
 * the diagram sits on the page rather than inside a box.
 */

type Observation = {
  id: string;
  logo: string;
  text: string;
};
/**
 * Two groups, each in its own oval: what Giga sweeps on a schedule, and what
 * your team says to it. Grouping is the only labelling the sources need — it
 * makes clear the memory has two independent origins.
 */
const SOURCE_GROUPS = [{
  label: 'Scans',
  note: 'On a schedule',
  logos: ['granola', 'gmail', 'salesforce', 'intercom', 'linear']
}, {
  label: 'Chats',
  note: 'As people work',
  logos: ['slack', 'giga', 'chatgpt', 'claude']
}];
const TRACKS = [{
  id: 'northwind',
  name: 'Northwind Labs',
  group: 'Giga Master',
  shared: true,
  count: 412,
  seed: [{
    id: 'nw-1',
    logo: 'granola',
    text: 'Champion unchanged after the pilot review'
  }, {
    id: 'nw-2',
    logo: 'salesforce',
    text: 'Opportunity in Legal review · $84k'
  }]
}, {
  id: 'onboarding',
  name: 'Onboarding revamp',
  group: 'Giga Master',
  shared: true,
  count: 268,
  seed: [{
    id: 'ob-1',
    logo: 'intercom',
    text: 'Setup is the top blocker, 31 conversations'
  }, {
    id: 'ob-2',
    logo: 'linear',
    text: 'CSV import bug shipped Tuesday'
  }]
}, {
  id: 'pipeline',
  name: 'Q3 pipeline',
  group: 'Giga Marketing',
  shared: true,
  count: 631,
  seed: [{
    id: 'pl-1',
    logo: 'gmail',
    text: 'Four renewal threads quiet over a week'
  }, {
    id: 'pl-2',
    logo: 'slack',
    text: 'Discount ceiling agreed at 15%'
  }]
}, {
  id: 'board',
  name: 'Board reporting',
  group: 'Faith’s Private Space',
  shared: false,
  count: 88,
  seed: [{
    id: 'bd-1',
    logo: 'salesforce',
    text: 'Q3 forecast down one slipped deal'
  }]
}];
type Event = {
  source: string;
  track: string;
  text: string;
  note?: string;
};
const EVENTS: Event[] = [{
  source: 'salesforce',
  track: 'pipeline',
  text: 'Acme moved to Legal review · closes 12 Sep'
}, {
  source: 'slack',
  track: 'northwind',
  text: 'Dana wants to be re-engaged in September',
  note: 'Sarah in #sales'
}, {
  source: 'granola',
  track: 'northwind',
  text: 'Pilot pushed to Q4 — budget freeze, not a loss'
}, {
  source: 'intercom',
  track: 'onboarding',
  text: 'Import errors now the second-most cited issue'
}, {
  source: 'giga',
  track: 'onboarding',
  text: 'We quote onboarding at 14 days, not 30',
  note: 'Marcus, Giga for web'
}, {
  source: 'gmail',
  track: 'pipeline',
  text: 'Harbour renewal quiet for nine days'
}, {
  source: 'claude',
  track: 'onboarding',
  text: 'Import runs behind a queue — retries are safe',
  note: 'Engineering, Claude Code'
}, {
  source: 'linear',
  track: 'onboarding',
  text: 'Setup wizard rewrite merged to main'
}, {
  source: 'chatgpt',
  track: 'board',
  text: 'Board wants NRR framed against logo churn',
  note: 'Faith, ChatGPT'
}, {
  source: 'salesforce',
  track: 'board',
  text: 'Committed ARR up $46k this week'
}];
const TICK_MS = 2000;
const FLIGHT_MS = 900;
export function BrainFlowExperiment() {
  const reduce = useReducedMotion();
  const shell = useRef<HTMLDivElement>(null);
  const sourceRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const trackRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const step = useRef(0);
  const [flight, setFlight] = useState<{
    key: number;
    from: {
      x: number;
      y: number;
    };
    to: {
      x: number;
      y: number;
    };
    event: Event;
  } | null>(null);
  const [contents, setContents] = useState<Record<string, Observation[]>>(() => Object.fromEntries(TRACKS.map(t => [t.id, t.seed])));
  const [counts, setCounts] = useState<Record<string, number>>(() => Object.fromEntries(TRACKS.map(t => [t.id, t.count])));
  useEffect(() => {
    if (reduce) return;
    let landing: number | undefined;
    const interval = window.setInterval(() => {
      const event = EVENTS[step.current % EVENTS.length];
      step.current += 1;
      const box = shell.current?.getBoundingClientRect();
      const a = sourceRefs.current[event.source]?.getBoundingClientRect();
      const b = trackRefs.current[event.track]?.getBoundingClientRect();
      if (!box || !a || !b) return;
      const key = step.current;
      setFlight({
        key,
        from: {
          x: a.left - box.left + a.width / 2,
          y: a.bottom - box.top
        },
        // Aim at the top of the list, where the new observation will appear.
        to: {
          x: b.left - box.left + b.width / 2,
          y: b.top - box.top + 66
        },
        event
      });
      landing = window.setTimeout(() => {
        setContents(prev => ({
          ...prev,
          [event.track]: [{
            id: `${key}`,
            logo: event.source,
            text: event.text
          }, ...prev[event.track]].slice(0, 3)
        }));
        setCounts(prev => ({
          ...prev,
          [event.track]: prev[event.track] + 1
        }));
        setFlight(null);
      }, FLIGHT_MS);
    }, TICK_MS);
    return () => {
      window.clearInterval(interval);
      if (landing) window.clearTimeout(landing);
    };
  }, [reduce]);
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  return <div className="mx-auto max-w-[1080px]" data-id="element-258" data-mp="3vwokovj8p2e2">
      <div className="mx-auto max-w-2xl text-center" data-id="element-259" data-mp="3vutw978res58">
        <p className="mb-4 text-[12px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-260" data-mp="386anyrppmfd5">
          Company Brain
        </p>
        <h2 className="font-title text-4xl font-bold leading-[1.05] tracking-tight text-[#2B211A] md:text-[56px]" data-id="element-261" data-mp="337zdqfvi62ue">
          Never wait for
          <br data-id="element-262" data-mp="1c3xvmwveoey5" />
          information again.
        </h2>
        <p className="mx-auto mt-5 max-w-[54ch] text-lg leading-relaxed text-[#6B5D52]" data-id="element-263" data-mp="1c2czx3xcupyp">
          Giga keeps a track for every project and area of the company. Scans sweep your tools on a schedule, your team's chats add what they learn, and Giga consolidates both into searchable memory.
        </p>
      </div>

      <div ref={shell} className="relative mt-12" data-id="element-264" data-mp="o8d1z3vvioy5">
        {/* Sources, grouped by origin. */}
        <div className="flex flex-wrap items-start justify-center gap-4 md:gap-8" data-id="element-265" data-mp="t7fuxpykx4pr">
          {SOURCE_GROUPS.map(group => <div key={group.label} className="relative rounded-[999px] border border-dashed border-[#DCD3C4] px-2.5 pb-2 pt-3.5 md:px-3.5" data-id="element-266" data-mp="y6sqcz3ink3y">
              <span className="absolute -top-[9px] left-1/2 -translate-x-1/2 whitespace-nowrap bg-white px-2 text-[10.5px] font-bold uppercase tracking-[0.12em] text-neutral-400" data-id="element-267" data-mp="29kd3krw6wpwk">
                {group.label}
                <span className="ml-1.5 font-medium normal-case tracking-normal text-neutral-300" data-id="element-268" data-mp="1wjpga07wirn">
                  {group.note}
                </span>
              </span>
              <div className="flex flex-wrap items-center justify-center gap-2.5 md:gap-3" data-id="element-269" data-mp="3padvn14u39ue">
                {group.logos.map(s => {
              const firing = flight?.event.source === s;
              return <div key={s} ref={el => {
                sourceRefs.current[s] = el;
              }} className={`flex size-12 items-center justify-center rounded-full border bg-white transition-all duration-200 md:size-14 ${firing ? 'border-[#EA580C]/60 shadow-[0_10px_28px_-14px_rgba(234,88,12,0.7)]' : 'border-[#E7E0D4]'}`} data-id="element-270" data-mp="3fd98axz958c0">
                      <img src={toolLogo(s)} alt="" aria-hidden="true" className="size-5 object-contain md:size-6" data-id="element-271" data-mp="31e9u6k9d8bu3" />
                    </div>;
            })}
              </div>
            </div>)}
        </div>

        {/* Crossing space — only the observation in flight belongs here. */}
        <div className="h-20 md:h-24" aria-hidden="true" data-id="element-272" data-mp="8han5m6mbxmu" />

        <div className="mb-2 flex items-center justify-between gap-2" data-id="element-273" data-mp="dfchj39hvjdm">
          <p className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.1em] text-neutral-400" data-id="element-274" data-mp="dej5pnvoi3om">
            <Folder size={11} strokeWidth={2.2} data-id="element-275" data-mp="3utzosruh6r0b" />
            Tracks
          </p>
          <p className="text-[11.5px] text-neutral-400" data-id="element-276" data-mp="3nohidyvgu0j">
            {total.toLocaleString()} observations kept
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-id="element-277" data-mp="3px8zrf0p0l2r">
          {TRACKS.map(t => <div key={t.id} ref={el => {
          trackRefs.current[t.id] = el;
        }} className={`rounded-2xl border bg-white p-3.5 transition-colors duration-200 ${flight?.event.track === t.id ? 'border-[#EA580C]/50 shadow-[0_18px_44px_-30px_rgba(234,88,12,0.55)]' : 'border-[#E7E0D4]'}`} data-id="element-278" data-mp="3bwya2fb4dcj6">
              <div className="flex items-start gap-2" data-id="element-279" data-mp="1f4o917deb4ti">
                <Folder size={14} strokeWidth={1.9} className="mt-0.5 shrink-0 text-neutral-400" data-id="element-280" data-mp="1f466jt053ud5" />
                <div className="min-w-0 flex-1" data-id="element-281" data-mp="2zn8k4t3hxc3j">
                  <p className="truncate text-[13px] font-semibold text-[#2B211A]" data-id="element-282" data-mp="2unvy2akg8920">
                    {t.name}
                  </p>
                  <p className="mt-0.5 flex items-center gap-1.5 truncate text-[11px] text-neutral-500" data-id="element-283" data-mp="39dlbp59qa5j6">
                    {t.shared ? <Users size={9} data-id="element-284" data-mp="34k8o8ygsc4s0" /> : <Lock size={9} data-id="element-285" data-mp="34jqpnv6nhegx" />}
                    {t.group}
                  </p>
                </div>
              </div>

              <p className="mt-2.5 flex items-baseline gap-1.5 border-t border-[#EFEAE1] pt-2.5" data-id="element-286" data-mp="3den4b3953jc6">
                <motion.span key={counts[t.id]} initial={{
              opacity: 0.4,
              y: -3
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.24
            }} className="font-title text-[17px] font-bold tabular-nums text-[#2B211A]" data-id="element-287" data-mp="3sbxpj4bl43l3">
                  {counts[t.id].toLocaleString()}
                </motion.span>
                <span className="text-[11px] text-neutral-400" data-id="element-288" data-mp="1vo52xlpa8zhx">
                  observations
                </span>
              </p>

              {/* Fixed height, fixed-height rows: the list is constantly
               swapping items of different lengths, and letting it size to its
               contents pushed the entire rest of the page up and down. */}
              <ul className="mt-2 h-[156px] space-y-1.5 overflow-hidden" data-id="element-289" data-mp="1gspdjtzndrrv">
                <AnimatePresence initial={false} data-id="element-290" data-mp="2edjxqyedksdv">
                  {contents[t.id].map((o, i) => <motion.li key={o.id} layout initial={{
                opacity: 0,
                scale: 0.96,
                y: -6
              }} animate={{
                opacity: i === 0 ? 1 : 0.6,
                scale: 1,
                y: 0
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.26,
                ease: [0.23, 1, 0.32, 1]
              }} className={`flex h-[48px] items-start gap-2 overflow-hidden rounded-xl border px-2.5 py-2 ${i === 0 ? 'border-[#EA580C]/30 bg-[#FFF8F3]' : 'border-[#EFEAE1]'}`} data-id="element-291" data-mp="29gbgr14yque7" data-mpi={i}>
                        <img src={toolLogo(o.logo)} alt="" aria-hidden="true" className="mt-[1px] size-3.5 shrink-0 object-contain" data-id="element-292" data-mp="34odosylh7f1k" />
                        <span className="min-w-0 overflow-hidden text-[11.5px] leading-snug text-[#3D3229]" style={{
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2
                }} data-id="element-293" data-mp="3em9w1fbalpue">
                          {o.text}
                        </span>
                      </motion.li>)}
                </AnimatePresence>
              </ul>
            </div>)}
        </div>

        {/* The observation in flight, and the path it takes. */}
        <AnimatePresence data-id="element-294" data-mp="66q72shnmtl5">
          {flight && <React.Fragment key={flight.key} data-id="element-295" data-mp="67rjb0aepm4g">
              <motion.svg className="pointer-events-none absolute inset-0 size-full" aria-hidden="true" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.2
          }} data-id="element-296" data-mp="b5krtzz6ms7w">
                <path d={`M${flight.from.x} ${flight.from.y} C ${flight.from.x} ${(flight.from.y + flight.to.y) / 2}, ${flight.to.x} ${(flight.from.y + flight.to.y) / 2}, ${flight.to.x} ${flight.to.y}`} fill="none" stroke="#EA580C" strokeOpacity={0.32} strokeWidth={1.4} strokeDasharray="3 4" data-id="element-297" data-mp="3rcmm6xvxptn4" />
              </motion.svg>

              <motion.div className="pointer-events-none absolute z-10 w-[220px] rounded-xl border border-[#EA580C]/40 bg-white px-2.5 py-1.5 shadow-[0_14px_30px_-14px_rgba(43,33,26,0.5)]" style={{
            left: 0,
            top: 0
          }} initial={{
            x: flight.from.x - 110,
            y: flight.from.y - 14,
            opacity: 0,
            scale: 0.96
          }} animate={{
            x: flight.to.x - 110,
            y: flight.to.y - 14,
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.98
          }} transition={{
            duration: FLIGHT_MS / 1000,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-298" data-mp="3veq1jxlq5ido">
                <span className="flex items-start gap-2" data-id="element-299" data-mp="grau4o3guwtp">
                  <img src={toolLogo(flight.event.source)} alt="" aria-hidden="true" className="mt-[1px] size-3.5 shrink-0 object-contain" data-id="element-300" data-mp="goolsm8r4daf" />
                  <span className="min-w-0" data-id="element-301" data-mp="7pl15dtxrptt">
                    <span className="block text-[11.5px] leading-snug text-[#2B211A]" data-id="element-302" data-mp="7rxiborvdw5t">
                      {flight.event.text}
                    </span>
                    {flight.event.note && <span className="mt-0.5 block text-[10.5px] text-neutral-400" data-id="element-303" data-mp="3tylz4rimwgeu">
                        {flight.event.note}
                      </span>}
                  </span>
                </span>
              </motion.div>
            </React.Fragment>}
        </AnimatePresence>
      </div>

    </div>;
}